// What a visitor on an English page (or another route without a locale
// currency) sees and pays, decided by Vercel's IP country header. The proxy's
// gg-currency cookie (which checkout reads) and getRequestCurrencyPrices (which
// the cards, hero buttons and login price line read) both call this, so the
// price on screen and the currency Stripe charges can't disagree (approval 346).
// Failure modes: tests/local-currency-en/failure-modes.md.

/** Countries whose own market sells on the same Stripe prices: their page locale and currency. */
export const ENGLISH_PAGE_LOCAL_MARKETS = {
  DK: { pricingLocale: "da", currency: "dkk" },
  SE: { pricingLocale: "sv", currency: "sek" },
  NO: { pricingLocale: "no", currency: "nok" },
} as const;

export const EU_COUNTRIES: ReadonlySet<string> = new Set([
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR",
  "DE", "GR", "HU", "IE", "IT", "LV", "LT", "LU", "MT", "NL",
  "PL", "PT", "RO", "SK", "SI", "ES", "SE",
]);

export interface EnglishPagePricing {
  /** The message locale whose Pricing namespace the cards read. */
  pricingLocale: "da" | "sv" | "no" | "de" | "en";
  /** The gg-currency cookie value checkout charges in. */
  currency: "dkk" | "sek" | "nok" | "eur" | "usd";
}

/**
 * The prices and checkout currency for an English page: Denmark, Sweden and
 * Norway get their own market's; the rest of the EU gets the euro prices;
 * everyone else, and requests without a country, the dollar prices.
 */
export function englishPagePricing(country: string | null | undefined): EnglishPagePricing {
  const code = country ?? "";
  if (Object.hasOwn(ENGLISH_PAGE_LOCAL_MARKETS, code)) {
    return ENGLISH_PAGE_LOCAL_MARKETS[code as keyof typeof ENGLISH_PAGE_LOCAL_MARKETS];
  }
  return EU_COUNTRIES.has(code)
    ? { pricingLocale: "de", currency: "eur" }
    : { pricingLocale: "en", currency: "usd" };
}

/** Every message locale an English page's cards can read prices from. */
export const ENGLISH_PAGE_PRICING_LOCALES = ["en", "de", "da", "sv", "no"] as const;

/**
 * The Premium upgrade and extension amount per checkout currency, as each
 * market's own UpgradeCard button states it (tests/local-currency-en ties every
 * label to that market's message and to the tracking amount). The English
 * dashboard shows the one for the gg-currency cookie checkout charges.
 */
export const ENGLISH_PAGE_UPGRADE_PRICES: Record<EnglishPagePricing["currency"], string> = {
  dkk: "200 kr",
  sek: "300 kr",
  nok: "310 kr",
  eur: "27 €",
  usd: "$32",
};

/** The upgrade amount for a gg-currency cookie value, or null for anything else. */
export function englishUpgradePrice(currency: string | null | undefined): string | null {
  return currency && Object.hasOwn(ENGLISH_PAGE_UPGRADE_PRICES, currency)
    ? ENGLISH_PAGE_UPGRADE_PRICES[currency as EnglishPagePricing["currency"]]
    : null;
}
